/**
 * Stop iOS zooming the page when a text field takes focus. Properly, this time.
 *
 * WHAT WAS ALREADY TRUE AND WAS NOT ENOUGH. Safari's documented rule is that it zooms when a
 * focused control computes under 16px, so every control in this app is 16px on a touch device —
 * enforced with `!important` inside `@media (pointer: coarse)`, resolved against the real cascade
 * by `touchFieldSize.test.ts`, and verified in a real browser at 91 fields across ten screens.
 * The page also has no horizontal overflow at 390px, so the layout viewport is exactly the device
 * width and the initial scale really is 1. Every condition the documented rule names is met.
 *
 * It still zoomed. So the rule is necessary and not sufficient, and no amount of further care
 * with font sizes was going to close it.
 *
 * WHAT THIS DOES INSTEAD. It removes the browser's room to zoom, for exactly as long as a text
 * field has focus, by adding `maximum-scale=1` to the viewport tag on the way in and taking it
 * off again on the way out. Zoom-on-focus cannot happen if there is no scale above 1 to go to.
 *
 * WHY THAT IS NOT THE THING I REFUSED TO DO EARLIER. Putting `maximum-scale=1, user-scalable=no`
 * in the page head is one line and stops the zoom by stopping ALL zoom — including somebody
 * pinching in to read a sixteen-round board on a phone, which is a thing managers actually do on
 * draft night. That trade is not worth making. This is the same clamp held for the two seconds
 * somebody is typing a name and released the moment they are done: pinch-zoom works everywhere,
 * all the time, except while a keyboard is up and covering half the screen anyway.
 *
 * The clamp goes on at `pointerdown`, before focus, rather than waiting for `focusin`. Safari
 * decides whether to zoom as part of processing the tap, and a viewport changed after that
 * decision is a viewport changed too late.
 */

import { isCoarsePointer } from './pointer.js';

/** What the page ships with. Must match the meta tag in `index.html`. */
export const VIEWPORT_BASE = 'width=device-width, initial-scale=1.0, viewport-fit=cover';

/** The same, with no room above 1. Held only while a text field has focus. */
export const VIEWPORT_CLAMPED =
  'width=device-width, initial-scale=1.0, maximum-scale=1.0, viewport-fit=cover';

/**
 * Controls that open a keyboard, which are the only ones Safari zooms for.
 *
 * By what it is NOT, rather than by a list of the types that qualify: a bare `<input>` defaults to
 * text and would be missed by an allow-list, and that is the commonest field in this app — every
 * `<input value={...} />` with no type attribute. The excluded types either open a picker of their
 * own or are not text at all.
 */
const NO_KEYBOARD = new Set([
  'checkbox', 'radio', 'range', 'color', 'file', 'button', 'submit', 'reset', 'image', 'hidden',
]);

export interface MaybeField {
  tagName?: string;
  type?: string | null;
  readOnly?: boolean;
  isContentEditable?: boolean;
}

export function isTextEntry(el: MaybeField | null | undefined): boolean {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = (el.tagName ?? '').toLowerCase();
  if (tag === 'textarea') return !el.readOnly;
  if (tag !== 'input') return false;
  // A read-only field can be focused and never opens a keyboard, so it never zooms.
  if (el.readOnly) return false;
  return !NO_KEYBOARD.has((el.type ?? 'text').toLowerCase());
}

/**
 * The clamp as a state machine, so the decision can be tested without a browser.
 *
 * Counted rather than boolean. Moving between two fields fires the new field's `pointerdown`
 * before the old field's `focusout`, and a boolean would be cleared by the second of those — the
 * clamp would come off in the middle of a tap into another text field, which is the exact moment
 * it is needed.
 */
export class ViewportClamp {
  private held = 0;

  /**
   * `base` is what the clamp puts BACK, and it is a constructor argument rather than the module
   * constant because it is not always the same string. In the installed app `noZoomGestures`
   * locks the viewport for the whole session; if this restored `VIEWPORT_BASE` regardless, a tap
   * into any text field would hand pinch-zoom back to an app that is meant to have none, and
   * hand it back permanently. So the caller says what normal is here.
   */
  constructor(
    private readonly apply: (content: string) => void,
    private readonly base: string = VIEWPORT_BASE,
  ) {}

  /** A text field is about to take, or has taken, focus. */
  hold(): void {
    this.held += 1;
    if (this.held === 1) this.apply(VIEWPORT_CLAMPED);
  }

  /** That field has let go. */
  release(): void {
    if (this.held === 0) return;
    this.held -= 1;
    if (this.held === 0) this.apply(this.base);
  }

  /** Nothing is focused at all — used to recover from a hold whose release never arrived. */
  reset(): void {
    if (this.held === 0) return;
    this.held = 0;
    this.apply(this.base);
  }

  get holds(): number {
    return this.held;
  }
}

/**
 * Wire it to the document. Returns an unsubscribe, and is a no-op anywhere it cannot help:
 * a mouse, a server render, a page with no viewport tag to rewrite.
 */
export function installNoZoomOnFocus(): () => void {
  if (typeof document === 'undefined') return () => {};
  if (!isCoarsePointer()) return () => {};
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return () => {};

  /*
   * Whatever the page is actually carrying right now, which is not always what it shipped with:
   * in the installed app `noZoomGestures` has already replaced it with a locked string, and that
   * is what "normal" means there.
   */
  const base = meta.getAttribute('content') ?? VIEWPORT_BASE;
  const clamp = new ViewportClamp((content) => meta.setAttribute('content', content), base);

  /*
   * `pointerdown` arms it and `focusin` catches everything else — a field focused by script, by a
   * hardware keyboard's Tab, by the keyboard accessory bar's next-field arrows. Both hold, and
   * each field's `focusout` releases once, so the count stays balanced.
   */
  const onDown = (e: Event) => {
    if (isTextEntry(e.target as MaybeField)) clamp.hold();
  };
  const onIn = (e: Event) => {
    if (isTextEntry(e.target as MaybeField)) clamp.hold();
  };
  const onOut = (e: Event) => {
    if (!isTextEntry(e.target as MaybeField)) return;
    // Two holds per field — the tap and the focus — so two releases. `focusout` fires once, so
    // the second comes from checking whether anything is still focused on the next frame.
    clamp.release();
    setTimeout(() => {
      const active = document.activeElement as MaybeField | null;
      if (!isTextEntry(active)) clamp.reset();
    }, 0);
  };

  document.addEventListener('pointerdown', onDown, true);
  document.addEventListener('focusin', onIn, true);
  document.addEventListener('focusout', onOut, true);

  return () => {
    document.removeEventListener('pointerdown', onDown, true);
    document.removeEventListener('focusin', onIn, true);
    document.removeEventListener('focusout', onOut, true);
    clamp.reset();
  };
}
